import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import { ImBarcode } from "react-icons/im";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdOutlineNavigateNext } from "react-icons/md";
import { formatCurrency } from "../../components/ProductPrice";
import Spinner from "../../components/Spinner";
import Modal from "../../components/Modal";
import BarCodeScanner from "../../components/BarCodeScanner";
import tacita from "../../assets/tacita.svg";
import {
  addToCart,
  clearCart,
  reduceQuantityFromCart,
  increaseQuantityInCart,
  removeFromCart,
} from "../../slices/appSlice";
import { clearCustomerData } from "../../slices/checkoutSlice";
import useAuthFetch from "../../hooks/useAuthFetch";
import Select from "../../components/Select";
import _ from "lodash";

const ROLES_POS = ["admin", "dependiente", "administradorTienda", "superAdmin"];
const POS_BODEGA_STORAGE_KEY = "retail-pos-bodega-id";

function getStoredBodegaId() {
  try {
    const v = localStorage.getItem(POS_BODEGA_STORAGE_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

function canUsePos(user) {
  if (!user || !user.token) return false;
  return (
    user.roles.some((r) => ROLES_POS.includes(r)) ||
    user.roles.indexOf("superAdmin") > -1
  );
}

// ponytail: O(n) variant set; upgrade path: server-side barcode normalize endpoint
function barcodeCodeVariants(code) {
  const trimmed = String(code).trim();
  if (!trimmed) return [];
  const variants = new Set([trimmed]);
  const stripped = trimmed.replace(/^0+/, "");
  if (stripped && stripped !== trimmed) variants.add(stripped);
  const base = stripped || trimmed;
  variants.add(base.padStart(12, "0"));
  variants.add(base.padStart(13, "0"));
  return [...variants];
}

function pickBarcodeMatch(products, stockByProduct) {
  if (!products?.length) return null;
  if (products.length === 1) return products[0];
  return (
    products.find((p) => (stockByProduct[p.id] ?? 0) > 0) ?? products[0]
  );
}

async function fetchProductsByBarcode(authFetch, code) {
  const variants = barcodeCodeVariants(code);
  if (!variants.length) return [];
  const orClause = variants.flatMap((c) => [
    { codigoBarras: c },
    { codigoProveedor: c },
  ]);
  const filter = encodeURIComponent(
    JSON.stringify({
      where: { and: [{ activo: 1 }, { or: orClause }] },
    })
  );
  const res = await authFetch(`producto-detalles?filter=${filter}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

const CATEGORY_FILTER = `{"include":[{"relation":"categorias","scope":{"fields":{"id":true,"idCategoria":true,"idCategoriaPadre":true},"include":[{"relation":"categoria","scope":{"fields":{"id":true,"nombre":true,"colorFondo":true,"imagen":true,"subTitulo":true}}}]}},{"relation":"marcas","scope":{"order":"orden asc","fields":{"createdAt":false,"updatedAt":false}}},{"relation":"categoria","scope":{"fields":{"id":true,"nombre":true,"colorFondo":true,"imagen":true}}},{"relation":"categoriaPadre","scope":{"fields":{"createdAt":false,"updatedAt":false},"include":[{"relation":"categoriaPadre","scope":{"fields":{"createdAt":false,"updatedAt":false},"include":[{"relation":"categoriaPadre","scope":{"fields":{"createdAt":false,"updatedAt":false},"include":[{"relation":"categoria","scope":{"fields":{"id":true,"nombre":true}}}]}},{"relation":"categoria","scope":{"fields":{"id":true,"nombre":true}}}]}},{"relation":"categoria","scope":{"fields":{"id":true,"nombre":true}}}]}}],"fields":{"createdAt":false,"updatedAt":false}}`;

function buildCategoryProductsFilter(idMarca) {
  return {
    include: [
      { relation: "categorias", scope: { fields: { id: true } } },
      {
        relation: "productos",
        scope: {
          fields: { createdAt: false, updatedAt: false },
          where: idMarca === "todas" ? { activo: 1 } : { idMarca, activo: 1 },
          order: ["nombreTienda asc", "precioVenta asc"],
          include: [{ relation: "descuentos" }],
        },
      },
      {
        relation: "familias",
        scope: {
          fields: { createdAt: false, updatedAt: false },
          where: idMarca === "todas" ? { activo: 1 } : { idMarca, activo: 1 },
          order: ["nombreTienda asc", "precioVenta asc"],
        },
      },
      { relation: "categoria", scope: { fields: { id: true, nombre: true } } },
    ],
    fields: { createdAt: false, updatedAt: false },
  };
}

export function RetailPosPage() {
  const dispatch = useDispatch();
  const authFetch = useAuthFetch();
  const cart = useSelector((state) => state.app.cart);
  const user = useSelector((state) => state.app.user);

  const [search, setSearch] = useState("");
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [bodegas, setBodegas] = useState([]);
  const [selectedBodegaId, setSelectedBodegaId] = useState(() => getStoredBodegaId());
  const [stockByProduct, setStockByProduct] = useState({});

  const [rootCategories, setRootCategories] = useState(null);
  const [categoryStack, setCategoryStack] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [selectedMarcaId, setSelectedMarcaId] = useState(null);
  const [products, setProducts] = useState(null);
  const [selectedMarcaName, setSelectedMarcaName] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchBodegas = useCallback(async () => {
    const res = await authFetch(
      `bodegas?filter={"where":{"activo":1},"order":["nombre asc"]}`
    );
    const data = await res.json();
    setBodegas(Array.isArray(data) ? data : []);
  }, [authFetch]);

  useEffect(() => {
    fetchBodegas();
  }, [fetchBodegas]);

  const fetchStockByBodega = useCallback(
    async (idBodega) => {
      if (!idBodega) {
        setStockByProduct({});
        return;
      }
      const res = await authFetch(
        `bodega-productos/bodega/${idBodega}/productos?filter={"limit": 5000}`
      );
      const data = await res.json();
      const map = {};
      (Array.isArray(data) ? data : []).forEach((item) => {
        map[item.idProducto] = item.cantidadEnBodega ?? 0;
      });
      setStockByProduct(map);
    },
    [authFetch]
  );

  useEffect(() => {
    fetchStockByBodega(selectedBodegaId || null);
  }, [selectedBodegaId, fetchStockByBodega]);

  useEffect(() => {
    if (selectedBodegaId != null) {
      try {
        localStorage.setItem(POS_BODEGA_STORAGE_KEY, String(selectedBodegaId));
      } catch (_) {}
    }
  }, [selectedBodegaId]);

  const fetchSearchResults = useCallback(
    async (searchText) => {
      if (!searchText.trim()) {
        setSearchResults(null);
        return;
      }
      setSearchLoading(true);
      setSearchResults(null);
      try {
        const filter = {
          include: [
            { relation: "familias", where: { idFamilia: null, activo: 1 } },
            { relation: "descuentos" },
          ],
        };
        const res = await authFetch(
          `producto-detalles/buscar/?filter=${JSON.stringify(filter)}`,
          {
            page: { current: 1, size: 50 },
            query: searchText.trim(),
          },
          "POST"
        );
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setSearchResults([...new Map(list.map((p) => [p.id, p])).values()]);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    if (!search.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    const t = setTimeout(() => {
      fetchSearchResults(search);
    }, 350);
    return () => clearTimeout(t);
  }, [search, fetchSearchResults]);

  const fetchRootCategories = useCallback(async () => {
    const data = await authFetch(
      `categoria-categoria-padre?filter={
        "where": { "idCategoriaPadre": null },
        "include": [{ "relation": "categoria" }]
      }`
    );
    const dataJSON = await data.json();
    setRootCategories(Array.isArray(dataJSON) ? dataJSON : []);
  }, [authFetch]);

  useEffect(() => {
    fetchRootCategories();
  }, [fetchRootCategories]);

  const fetchCategory = useCallback(
    async (id) => {
      const res = await authFetch(
        `categoria-categoria-padre/${id}/?filter=${CATEGORY_FILTER}`
      );
      const data = await res.json();
      return data;
    },
    [authFetch]
  );

  const fetchCategoryWithProducts = useCallback(
    async (idCategoria, idMarca) => {
      const filter = buildCategoryProductsFilter(idMarca);
      const res = await authFetch(
        `categoria-categoria-padre/${idCategoria}/?filter=${JSON.stringify(filter)}`
      );
      const data = await res.json();
      const productos = data.productos || [];
      const familias = data.familias || [];
      setProducts([...productos, ...familias]);
    },
    [authFetch]
  );

  const goToSubCategory = useCallback(
    async (subCat) => {
      const data = await fetchCategory(subCat.id);
      setCategoryStack((prev) => [...prev, { id: currentCategory.id, data: currentCategory }]);
      setCurrentCategory(data);
    },
    [fetchCategory, currentCategory]
  );

  const goToMarca = useCallback(
    (idMarca, nombre) => {
      if (!selectedBodegaId) {
        return;
      }
      setSelectedMarcaId(idMarca);
      setSelectedMarcaName(nombre);
      setProducts(null);
      fetchCategoryWithProducts(currentCategory.id, idMarca);
    },
    [currentCategory, fetchCategoryWithProducts, selectedBodegaId]
  );

  const goBack = useCallback(() => {
    if (selectedMarcaId !== null) {
      setSelectedMarcaId(null);
      setSelectedMarcaName(null);
      setProducts(null);
      return;
    }
    if (search.trim()) {
      setSearch("");
      setSearchResults(null);
      return;
    }
    if (categoryStack.length === 0) {
      setCurrentCategory(null);
      return;
    }
    const next = categoryStack[categoryStack.length - 1];
    setCategoryStack((prev) => prev.slice(0, -1));
    setCurrentCategory(next.data);
  }, [selectedMarcaId, categoryStack, search]);

  const handleSearchSubmit = useCallback(
    (e) => {
      e?.preventDefault?.();
      if (selectedBodegaId && search.trim()) {
        fetchSearchResults(search);
      }
    },
    [selectedBodegaId, search, fetchSearchResults]
  );

  const openRootCategory = useCallback(
    async (cat) => {
      setCurrentCategory(null);
      setCategoryStack([]);
      const data = await fetchCategory(cat.id);
      setCurrentCategory(data);
    },
    [fetchCategory]
  );

  const showProductGrid = selectedMarcaId !== null;
  const isSearchActive = search.trim().length > 0;
  const productsToDisplay = isSearchActive ? searchResults : products;
  const filteredProducts = productsToDisplay;

  function PosBreadcrumb() {
    const items = [];
    let temp = currentCategory?.categoriaPadre;
    while (temp) {
      items.unshift(
        <span key={`nav-${temp.id}`} className="font-title text-lg sm:text-2xl md:text-3xl uppercase text-white">
          {temp.categoria?.nombre}
        </span>,
        <MdOutlineNavigateNext key={`sep-${temp.id}`} className="inline text-white text-lg sm:text-2xl md:text-3xl" />
      );
      temp = temp.categoriaPadre;
    }
    if (currentCategory) {
      items.push(
        <span key="current" className="font-title text-lg sm:text-2xl md:text-3xl uppercase text-white">
          {currentCategory.categoria?.nombre}
        </span>
      );
    }
    if (selectedMarcaName) {
      items.push(
        <MdOutlineNavigateNext key="sep-marca" className="inline text-white text-lg sm:text-2xl md:text-3xl" />,
        <span key="marca" className="font-title text-lg sm:text-2xl md:text-3xl uppercase text-white">
          {selectedMarcaName}
        </span>
      );
    }
    return <div className="flex flex-wrap items-center gap-1">{items}</div>;
  }

  const getEffectiveStock = (p) =>
    selectedBodegaId ? (stockByProduct[p.id] ?? 0) : (p.cantidadEnBodega ?? 0);

  const handleAddProduct = (product, delta = 1) => {
    const currentQty = cart.products[product.id]?.quantity || 0;
    const maxStock = getEffectiveStock(product);
    const newQty = Math.max(0, Math.min(currentQty + delta, maxStock));
    if (newQty === 0) return;
    const productWithStock = {
      ...product,
      cantidadEnBodega: maxStock,
    };
    dispatch(
      addToCart({
        product: productWithStock,
        quantity: newQty - currentQty,
        dailyConsumption: 0,
        whatsappConfirmation: false,
        estimatedOutOfFoodDate: "",
        dontOpenCart: true,
      })
    );
  };

  const cartItems = Object.entries(cart.products || {});
  const selectedBodegaName = selectedBodegaId != null
    ? bodegas.find((b) => b.id === selectedBodegaId)?.nombre
    : null;

  if (!canUsePos(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-backgroundGray p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <p className="text-baseGray text-lg mb-4">
            No tenés permiso para usar el punto de venta.
          </p>
          <Link
            to="/tienda"
            className="inline-block bg-purple text-white py-2 px-6 rounded-full"
          >
            Ir a la tienda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-backgroundGray">
      {showBarcodeScanner && (
        <Modal
          backgroundColor="bg-purple"
          open={true}
          closeModalFunction={() => {
            setShowBarcodeScanner(false);
          }}
        >
          <div className="flex flex-wrap justify-center items-center p-8">
            <p className="text-white mb-4 w-full">
              Escaneá el código de barras del producto:
            </p>
            <div style={{ marginTop: 30, width: 640, height: 320 }}>
              <BarCodeScanner
                onUpdate={async (result) => {
                  if (result && result.codeResult && result.codeResult.code) {
                    try {
                      const code = result.codeResult.code;
                      const matches = await fetchProductsByBarcode(
                        authFetch,
                        code
                      );
                      const product = pickBarcodeMatch(matches, stockByProduct);
                      if (product) {
                        handleAddProduct(product, 1);
                        setSearch(product.nombreTienda || "");
                      }
                    } catch (e) {
                      // en POS simplemente ignoramos errores de escaneo
                    }
                    setShowBarcodeScanner(false);
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      )}
      {/* Product grid */}
      <div className="flex-1 flex flex-col min-h-0 lg:overflow-hidden">
        <div className="shrink-0 flex flex-wrap items-center gap-3 p-3 bg-backgroundGray border-b border-gray">
          <form
            className="w-full lg:flex-1 lg:min-w-0 relative min-w-0"
            onSubmit={handleSearchSubmit}
          >
            <input
              type="search"
              placeholder="Buscar producto, marca o código..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!selectedBodegaId}
              className="w-full rounded-full border border-borderGray bg-white pl-12 pr-12 py-3 text-base text-gray-800 placeholder:text-gray-500 outline-none focus:border-purple disabled:opacity-60 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={() => setShowBarcodeScanner(true)}
              className="absolute left-3 top-2.5 p-1.5"
              aria-label="Escanear código de barras"
            >
              <ImBarcode className="text-2xl text-borderGray" />
            </button>
            <button
              type="button"
              onClick={handleSearchSubmit}
              disabled={!selectedBodegaId || !search.trim()}
              className="absolute right-3 top-2.5 p-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Buscar"
            >
              <BsSearch className="text-2xl text-borderGray" />
            </button>
          </form>
          <div className="w-full lg:w-auto flex items-center gap-3 justify-between lg:min-w-0">
            <div className="min-w-0 flex-1 lg:flex-initial lg:min-w-[160px]">
              <Select
                label=""
                labelColor="text-baseGray"
                options={bodegas.map((b) => ({ value: b.id, label: b.nombre }))}
                value={selectedBodegaId}
                onChange={(val) => setSelectedBodegaId(val)}
                containerClasses="mb-0"
                buttonClasses="rounded-full border border-borderGray h-10 py-2 text-sm min-w-[140px]"
              />
            </div>
            <Link
              to="/"
              className="py-2 px-4 rounded-full bg-purple text-white font-body text-sm font-semibold hover:opacity-90 active:opacity-90 shrink-0"
            >
              Salir a tienda
            </Link>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 lg:p-4 min-h-0">
          {!selectedBodegaId ? (
            <div className="flex items-center justify-center min-h-full min-w-0">
              <p className="text-baseGray text-xl text-center font-body px-4">
                Debe seleccionar una bodega para poder continuar.
              </p>
            </div>
          ) : (
            <>
          {(showProductGrid || currentCategory || categoryStack.length > 0 || search.trim()) && (
            <div className="flex items-center justify-between gap-2 mb-3">
              <button
                type="button"
                onClick={goBack}
                className="shrink-0 flex items-center gap-1 py-2 px-4 rounded-full bg-pink text-white font-body text-sm font-bold hover:opacity-90"
              >
                <IoMdArrowRoundBack className="text-lg" /> Volver
              </button>
            </div>
          )}

          {!currentCategory && !showProductGrid && !search.trim() && (
            <>
              {!rootCategories ? (
                <div className="flex items-center justify-center min-h-[200px]">
                  <Spinner />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {rootCategories.map((cat, index) => {
                    const roles = user?.roles || [];
                    const canSee = cat.categoria?.principal || _.intersection(roles, ["admin", "dependiente", "administradorTienda"]).length > 0 || roles.indexOf("superAdmin") > -1;
                    if (!canSee) return null;
                    return (
                      <button
                        key={`root_${cat.id}_${index}`}
                        type="button"
                        onClick={() => openRootCategory(cat)}
                        className={`cursor-pointer w-full sm:w-[calc(50%-0.25rem)] flex flex-[1_1_calc(50%-0.5rem)] items-center ${cat.categoria?.colorFondo || "bg-gray"} py-4 px-6 rounded-3xl border-0 text-left`}
                      >
                        <div className="flex items-center w-full">
                          <div className="w-8/12 md:w-7/12">
                            <p className="text-white font-title text-lg sm:text-2xl uppercase">
                              {cat.categoria?.nombre}
                            </p>
                          </div>
                          <div className="w-4/12 md:w-5/12 pl-2 flex justify-end">
                            {cat.categoria?.imagen && (
                              <img
                                className="max-h-24 md:max-h-32"
                                src={cat.categoria.imagen}
                                alt=""
                              />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {currentCategory && !showProductGrid && !search.trim() && (
            <>
              <div
                className={`${currentCategory.categoria?.colorFondo || "bg-gray"} flex items-center w-full p-4 rounded-3xl mb-4`}
              >
                <div className="w-11/12">
                  <PosBreadcrumb />
                </div>
                <div className="w-1/12">
                  {currentCategory.categoria?.imagen && (
                    <img
                      className="max-h-14 float-right"
                      src={currentCategory.categoria.imagen}
                      alt=""
                    />
                  )}
                </div>
              </div>
              {currentCategory.categorias && currentCategory.categorias.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {currentCategory.categorias.map((cat, index) => (
                    <button
                      key={`sub_${cat.id}_${index}`}
                      type="button"
                      onClick={() => goToSubCategory(cat)}
                      className={`cursor-pointer min-h-[5rem] sm:min-h-[7rem] flex flex-[0_0_calc(50%-0.5rem)] sm:flex-[0_0_calc(33.333%-0.5rem)] justify-center items-center ${cat.categoria?.colorFondo || "bg-gray"} py-4 px-6 rounded-3xl border-0 text-center`}
                    >
                      <p className="text-white font-title text-sm sm:text-xl md:text-2xl uppercase">
                        {cat.categoria?.nombre}
                        {cat.categoria?.subTitulo && (
                          <>
                            <br />
                            <span className="text-white/90">{cat.categoria.subTitulo}</span>
                          </>
                        )}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {currentCategory.marcas && currentCategory.marcas.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {!selectedBodegaId && (
                    <p className="w-full text-sm text-baseGray mb-1">
                      Seleccioná una bodega arriba para poder cargar productos por marca.
                    </p>
                  )}
                  {currentCategory.marcas.map((m, index) => (
                    <button
                      key={`marca_${m.id}_${index}`}
                      type="button"
                      onClick={() => goToMarca(m.id, m.nombreTienda || m.nombre)}
                      disabled={!selectedBodegaId}
                      className="cursor-pointer h-28 sm:h-32 flex flex-[0_0_calc(50%-0.5rem)] sm:flex-[0_0_calc(25%-0.5rem)] lg:flex-[0_0_calc(20%-0.5rem)] justify-center items-center bg-white border border-purple py-4 px-4 rounded-2xl hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {m.logo ? (
                        <img
                          className="max-h-20 sm:max-h-24 object-contain"
                          src={m.logo}
                          alt={m.nombreTienda || m.nombre}
                        />
                      ) : (
                        <span className="text-baseGray font-semibold text-center">
                          {m.nombreTienda || m.nombre}
                        </span>
                      )}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => goToMarca("todas", "Todas las marcas")}
                    disabled={!selectedBodegaId}
                    className="cursor-pointer h-28 sm:h-32 flex flex-[0_0_calc(50%-0.5rem)] sm:flex-[0_0_calc(25%-0.5rem)] lg:flex-[0_0_calc(20%-0.5rem)] justify-center items-center bg-purple border border-purple py-4 px-6 rounded-3xl text-white font-body text-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Ver todas las marcas
                  </button>
                </div>
              )}
              <p className="mt-4 text-sm text-baseGray">
                La segmentación de nuestra tienda por etapa de la vida y talla se realiza según las presentaciones de las marcas que ponemos a tu disposición. Si tenés dudas sobre como clasificar a tu mascota consultá al médico veterinario de tu confianza.
              </p>
            </>
          )}

          {(showProductGrid || search.trim()) && (
            <>
              {search.trim() && !showProductGrid ? (
                <div className="bg-gray-200 flex items-center w-full p-4 rounded-3xl mb-4 border border-gray-300">
                  <p className="font-title text-lg uppercase text-gray-800">
                    Búsqueda: {search.trim()}
                  </p>
                </div>
              ) : currentCategory && showProductGrid ? (
                <div
                  className={`${currentCategory.categoria?.colorFondo || "bg-gray"} flex items-center w-full p-4 rounded-3xl mb-4`}
                >
                  <div className="w-full">
                    <PosBreadcrumb />
                  </div>
                </div>
              ) : null}
              {isSearchActive && searchLoading ? (
                <div className="flex items-center justify-center min-h-[200px]">
                  <Spinner />
                </div>
              ) : !filteredProducts ? (
                <div className="flex items-center justify-center min-h-[200px]">
                  <Spinner />
                </div>
              ) : filteredProducts.length === 0 ? (
                <p className="text-baseGray text-center py-8">
                  {isSearchActive
                    ? "No hay resultados para la búsqueda."
                    : "No hay productos que coincidan con la búsqueda."}
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 lg:gap-3">
                  {filteredProducts.map((p) => {
                    const qty = cart.products[p.id]?.quantity || 0;
                    const effectiveStock = selectedBodegaId
                      ? (stockByProduct[p.id] ?? 0)
                      : (p.cantidadEnBodega ?? 0);
                    const inStock = effectiveStock > 0;
                    return (
                      <div
                        key={p.id}
                        className={`rounded-xl border-2 overflow-hidden flex flex-col bg-white transition shadow-sm ${
                          inStock
                            ? "border-gray hover:border-purple cursor-pointer"
                            : "border-gray opacity-60 cursor-not-allowed"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => inStock && handleAddProduct(p, 1)}
                          disabled={!inStock}
                          className="flex-1 flex flex-col items-center p-3 text-left w-full min-h-[140px]"
                        >
                          {p.fotoPrincipal ? (
                            <img
                              src={p.fotoPrincipal}
                              alt={p.nombreTienda}
                              className="w-full aspect-square object-contain rounded-lg bg-gray"
                            />
                          ) : (
                            <div className="w-full aspect-square rounded-lg bg-gray flex items-center justify-center text-baseGray text-sm">
                              Sin foto
                            </div>
                          )}
                          <p className="mt-2 text-sm font-body font-semibold text-baseGray line-clamp-2 w-full">
                            {p.marca} {p.nombreTienda}
                          </p>
                          {Number(p.pesoGramos) > 0 && (
                            <p className="text-xs text-borderGray w-full mt-0.5">
                              Presentación:{" "}
                              <span className="font-semibold">
                                {p.pesoGramos < 1000
                                  ? `${p.pesoGramos} gramos`
                                  : `${(p.pesoGramos / 1000).toFixed(2)} Kg`}
                              </span>
                            </p>
                          )}
                          <p className="text-purple font-bold text-base mt-1 w-full">
                            {formatCurrency(p.precioVenta)}
                          </p>
                          {(selectedBodegaId ? effectiveStock != null : p.cantidadEnBodega != null) && (
                            <p className="text-xs text-borderGray w-full">
                              Stock: {effectiveStock}
                            </p>
                          )}
                        </button>
                        <div className="flex items-center justify-between gap-1 p-2 bg-gray border-t border-gray">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (qty > 0) {
                                if (qty === 1) dispatch(removeFromCart(p.id));
                                else dispatch(reduceQuantityFromCart(p.id));
                              }
                            }}
                            disabled={qty === 0}
                            className="w-10 h-10 rounded-lg bg-white border border-borderGray flex items-center justify-center text-xl font-bold text-baseGray disabled:opacity-40 disabled:cursor-not-allowed active:bg-gray"
                            aria-label="Quitar uno"
                          >
                            −
                          </button>
                          <span className="text-base font-bold text-baseGray min-w-[2rem] text-center">
                            {qty}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (inStock) handleAddProduct(p, 1);
                            }}
                            disabled={!inStock}
                            className="w-10 h-10 rounded-lg bg-purple text-white flex items-center justify-center text-xl font-bold disabled:opacity-40 disabled:cursor-not-allowed active:bg-purple-border"
                            aria-label="Agregar uno"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
            </>
          )}
        </div>
      </div>

      {/* Cart panel */}
      <aside className="w-full lg:w-[360px] xl:w-[400px] flex flex-col bg-purple text-white shrink-0 border-t lg:border-t-0 lg:border-l border-purple-border">
        <div className="p-3 lg:p-4 border-b border-white/20">
          <div className="flex items-center">
            <img
              className="brightness-[500] w-9 h-9 lg:w-12 lg:h-12 shrink-0"
              src={tacita}
              alt="Tacita de compra"
            />
            <span className="text-white m-1.5 lg:m-2 text-sm lg:text-base">Tacita de compra</span>
          </div>
          {selectedBodegaName && (
            <p className="text-white font-semibold text-xs lg:text-sm mt-1.5 lg:mt-2 px-1 py-1 lg:py-1.5 rounded-lg bg-white/20">
              Comprando para: {selectedBodegaName}
            </p>
          )}
          <p className="text-white/80 text-xs lg:text-sm mt-0.5 lg:mt-1">
            {cart.productsCount} {cart.productsCount === 1 ? "producto" : "productos"}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <p className="text-white/70 text-sm">
              Agregá productos tocando en la grilla o usando + en cada uno.
            </p>
          ) : (
            <ul className="space-y-3">
              {cartItems.map(([productId, line]) => {
                const { product, quantity } = line;
                const lineMaxStock =
                  selectedBodegaId
                    ? (stockByProduct[product.id] ?? 0)
                    : (product.cantidadEnBodega ?? 0);
                const canIncrease = quantity < lineMaxStock;
                return (
                  <li
                    key={productId}
                    className="flex items-center gap-3 bg-white/10 rounded-xl p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">
                        {product.nombreTienda}
                      </p>
                      {Number(product.pesoGramos) > 0 && (
                        <p className="text-white/70 text-xs">
                          Presentación:{" "}
                          {product.pesoGramos < 1000
                            ? `${product.pesoGramos} gramos`
                            : `${(product.pesoGramos / 1000).toFixed(2)} Kg`}
                        </p>
                      )}
                      <p className="text-white/80 text-xs">
                        {quantity} × {formatCurrency(product.precioVenta)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (quantity === 1) dispatch(removeFromCart(product.id));
                          else dispatch(reduceQuantityFromCart(product.id));
                        }}
                        className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-lg font-bold active:bg-white/30"
                        aria-label="Menos"
                      >
                        −
                      </button>
                      <span className="w-8 text-center font-bold">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => canIncrease && dispatch(increaseQuantityInCart(product.id))}
                        disabled={!canIncrease}
                        className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-lg font-bold active:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Más"
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => dispatch(removeFromCart(product.id))}
                      className="text-white/80 hover:text-white p-1"
                      aria-label="Quitar"
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="p-4 border-t border-white/20 space-y-3">
          <div className="flex justify-between text-lg">
            <span>Subtotal</span>
            <span className="font-bold">
              {formatCurrency(cart.totalWithAnyDiscount || cart.total)}
            </span>
          </div>
          {cart.productsCount > 0 ? (
            <Link
              to="/checkout-pos"
              className="block w-full py-4 rounded-xl bg-orange text-white font-bold text-center text-lg active:opacity-90"
            >
              Ir a cobrar
            </Link>
          ) : (
            <span className="block w-full py-4 rounded-xl bg-orange/50 text-white font-bold text-center text-lg cursor-not-allowed">
              Ir a cobrar
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              dispatch(clearCart());
              dispatch(clearCustomerData());
              setCategoryStack([]);
              setCurrentCategory(null);
              setSelectedMarcaId(null);
              setSelectedMarcaName(null);
              setProducts(null);
              setSearch("");
              setSearchResults(null);
            }}
            disabled={cart.productsCount === 0}
            className="block w-full py-3 rounded-xl border-2 border-white/50 text-white font-body disabled:opacity-50 disabled:cursor-not-allowed active:bg-white/10"
          >
            Nueva venta
          </button>
        </div>
      </aside>
    </div>
  );
}

export default RetailPosPage;
